import {
  AmbianceEnum,
  FilterSearchDto,
  FiltersEnum,
  HouseSearchFragment,
  HouseTypeEnum,
  NavigationTargetEnum,
  TravelTypeEnum,
} from '../lib/graphql'

interface IComputeHouseAppExplorerUrlParamsProps {
  search?: Omit<FilterSearchDto, 'prompt'>
  isActiveSearch?: boolean
  ownerToken?: string
  parentLogId?: string
}

export type IHouseExplorerUrlProps = {
  _id: string
  pathname?: string | null
  index: number
  suggestion?: HouseSearchFragment['suggestion']
  target: NavigationTargetEnum
  travelType?: TravelTypeEnum
}

export interface IComputeHouseAppExplorerUrlProps {
  house: Omit<
    IHouseExplorerUrlProps,
    'index' | 'suggestion' | 'target' | 'travelType'
  >
  urlParams?: IComputeHouseAppExplorerUrlParamsProps
  isSeminar: boolean
  domain: string | undefined
}

function addSubdomain(url: string, subdomain?: string): string {
  if (!subdomain) {
    return url
  }

  try {
    // Parse the URL
    const urlObject = new URL(url)

    // Extract hostname, port, and protocol
    const { hostname, port, protocol } = urlObject

    // Split the hostname into parts
    const hostnameParts = hostname.split('.')

    // Remove "www" if it exists
    if (hostnameParts[0].toLowerCase() === 'www') {
      hostnameParts.shift()
    }

    // Add the subdomain to the hostname
    hostnameParts.unshift(subdomain)
    const newHostname = hostnameParts.join('.')

    // Construct the new URL
    const portSegment = port ? `:${port}` : ''
    const newUrl = `${protocol}//${newHostname}${portSegment}`

    return newUrl
  } catch {
    return url
  }
}

export const computeHouseAppExplorerUrl = ({
  house,
  urlParams = {},
  isSeminar,
  domain,
}: IComputeHouseAppExplorerUrlProps): string => {
  const { search, ownerToken, parentLogId } = urlParams
  const baseUrl = domain
    ? addSubdomain(process.env.NEXT_PUBLIC_APP_URL as string, domain)
    : process.env.NEXT_PUBLIC_APP_URL

  const url = new URL(
    `${baseUrl}${getHousePathname({ isSeminar })}/${house._id}`,
  )
  // hash
  url.hash = house.pathname ?? ''

  // queryString aka searchParams
  if (search) {
    const { adults, travelType, dates, location } = search

    if (adults) {
      url.searchParams.append('adults', String(adults))
    }

    if (dates) {
      const { endDate, startDate } = dates

      if (endDate && startDate) {
        url.searchParams.append('startDate', String(startDate.getTime()))
        url.searchParams.append('endDate', String(endDate.getTime()))
      }
    }

    if (travelType) {
      url.searchParams.append('travelType', travelType)
    }

    if (location?.placeId) {
      const { placeId } = location

      url.searchParams.append('location', placeId)
    }

    if (parentLogId) {
      url.searchParams.append('parentLogId', parentLogId)
    }
  }

  if (ownerToken) {
    url.searchParams.append('ownerToken', ownerToken)
  }

  return url.toString()
}

export type CollectionSearchParams = {
  filters?: FiltersEnum[]
  travelType?: TravelTypeEnum
  ambiance?: AmbianceEnum[]
  houseType?: HouseTypeEnum[]
  placeId?: string
}

export const getCollectionPathname = ({
  isSeminar,
  filters,
  travelType,
  ambiance,
  houseType,
  placeId,
}: {
  isSeminar?: boolean
} & CollectionSearchParams) => {
  const url = new URL('https://naboo.app/collection') // host and protocol do not matter, will be ignore anyway
  if (filters) {
    url.searchParams.set('filters', filters.join(','))
  }

  if (travelType) {
    url.searchParams.set('travelType', travelType)
  }

  if (ambiance) {
    url.searchParams.set('ambiance', ambiance.join(','))
  }

  if (houseType) {
    url.searchParams.set('houseType', houseType.join(','))
  }

  if (placeId) {
    url.searchParams.set('location', placeId)
  }

  if (isSeminar) {
    url.pathname = '/seminaire/collection'
  }
  return url.pathname + url.search
}

export const getHomePathname = ({ isSeminar }: { isSeminar?: boolean }) => {
  if (isSeminar) {
    return '/seminaire'
  }
  return '/'
}

export const getHousePathname = ({ isSeminar }: { isSeminar?: boolean }) => {
  if (isSeminar) {
    return '/seminaire/houses'
  }
  return '/explorer/houses'
}
